/**
 * ADR-086 — the messenger transport, ATO Android's local implementation.
 *
 * Posts an envelope to `POST /api/v1/messenger` on the parts worker, which
 * holds the destination credentials. This client knows a destination NAME and
 * nothing else, which is the point: rotating a webhook or moving a channel
 * redeploys nothing here.
 *
 * Local rather than `@autoviz-io/messenger` on purpose. ADR-086 defers the
 * package until a second consumer exists, so the envelope is settled by two
 * real callers rather than one imagined one, and no immutable version is minted
 * against a shape that has been used once.
 *
 * Three properties are non-negotiable, because this runs on the failure path:
 *
 *   1. **Fire-and-forget.** Returns void, never throws, never retries. A void
 *      return is deliberate — it means no call site can accidentally await it
 *      and turn a messenger fault into the incident being reported.
 *   2. **`sendBeacon` first.** The session summary fires as the tab is closing,
 *      which is exactly when an ordinary request is killed mid-flight.
 *   3. **Silent.** A transport failure here is not worth a console error on a
 *      user's phone, and certainly not worth a toast.
 */

import { partsApiBaseURL } from '@/models/app-environment'
import type { AppEnvironment } from '@/models/app-environment'
import type { MessengerMessage } from '@/services/session-report'

let baseUrl: string | null = null

/** Point the client at an environment's parts worker. Called once at boot;
 *  until then `send` is inert, so an early failure cannot fire at a host we
 *  have not resolved yet. */
export function configureMessenger(env: AppEnvironment): void {
  baseUrl = partsApiBaseURL(env)
}

export function send(message: MessengerMessage): void {
  if (baseUrl === null) return
  const url = `${baseUrl}/api/v1/messenger`
  // Auto-filled rather than per-call-site: a link that depends on each caller
  // remembering is a link half the messages will not have. The whole href —
  // the wheel code and env in the query are what relaunch the session.
  const sourceUrl =
    message.sourceUrl ??
    (typeof location !== 'undefined' && location.href !== '' ? location.href : undefined)
  const body = JSON.stringify(
    sourceUrl == null ? message : { ...message, sourceUrl },
  )

  try {
    // sendBeacon survives pagehide, which is when the summary fires. It also
    // cannot set a content-type header, so the body goes as a Blob carrying
    // one — without it the worker sees text/plain and Hono's json parsing
    // would never run.
    const beacon = navigator.sendBeacon?.bind(navigator)
    if (beacon != null) {
      const blob = new Blob([body], { type: 'application/json' })
      if (beacon(url, blob)) return
      // A false return means the payload was refused (queue full, too large).
      // Fall through rather than lose the report.
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      // Survives the document being torn down, same reason as sendBeacon.
      keepalive: true,
      // The endpoint is origin-gated; credentials are neither needed nor sent.
      credentials: 'omit',
    }).catch(() => {
      // Deliberately silent — see the module comment.
    })
  } catch {
    // Deliberately silent.
  }
}
